/**
 * Editor menu state hook for DocumentEditor.
 *
 * Uses Tiptap's `useEditorState` with a selector function so that
 * React only re-renders when the derived boolean flags actually change
 * (not on every transaction).
 *
 * Section context uses our generic node names (sectionTitle, paragraphSection,
 * richSection, atomicField) instead of entity-specific names — the same
 * toolbar logic works for all 6 entity types.
 */

import type { Editor } from "@tiptap/react";
import { useEditorState } from "@tiptap/react";
import { HIGHLIGHT_COLORS, type HighlightColor } from "../../extensions/highlight-mark";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface DocumentMenuState {
  // Inline marks
  isBold: boolean;
  canBold: boolean;
  isItalic: boolean;
  canItalic: boolean;
  isStrike: boolean;
  canStrike: boolean;
  isCode: boolean;
  canCode: boolean;
  isHighlight: boolean;
  canHighlight: boolean;
  highlightColor: HighlightColor | null;

  // Block nodes
  isHeading2: boolean;
  isHeading3: boolean;
  isBulletList: boolean;
  isOrderedList: boolean;
  isCodeBlock: boolean;
  canCodeBlock: boolean;
  codeBlockLanguage: string;
  isBlockquote: boolean;

  // Section context (which generic node type the cursor is in)
  isSectionTitle: boolean;
  isParagraphSection: boolean;
  isRichSection: boolean;
  isAtomicField: boolean;

  // History
  canUndo: boolean;
  canRedo: boolean;
}

// ─── Default State ───────────────────────────────────────────────────────────

const defaultState: DocumentMenuState = {
  isBold: false,
  canBold: false,
  isItalic: false,
  canItalic: false,
  isStrike: false,
  canStrike: false,
  isCode: false,
  canCode: false,
  isHighlight: false,
  canHighlight: false,
  highlightColor: null,
  isHeading2: false,
  isHeading3: false,
  isBulletList: false,
  isOrderedList: false,
  isCodeBlock: false,
  canCodeBlock: false,
  codeBlockLanguage: "plaintext",
  isBlockquote: false,
  isSectionTitle: false,
  isParagraphSection: false,
  isRichSection: false,
  isAtomicField: false,
  canUndo: false,
  canRedo: false,
};

function canToggleCodeBlock(editor: Editor): boolean {
  const chain = (editor.can() as unknown as { chain?: () => Record<string, unknown> }).chain?.();
  if (!chain) return false;

  const toggleCodeBlock = chain.toggleCodeBlock;
  const run = chain.run;
  if (typeof toggleCodeBlock !== "function" || typeof run !== "function") {
    return false;
  }

  return (toggleCodeBlock as () => { run: () => boolean }).call(chain).run();
}

function activeHighlightColor(editor: Editor): HighlightColor | null {
  if (!editor.isActive("highlight")) return null;
  const color = editor.getAttributes("highlight").color;
  return HIGHLIGHT_COLORS.find((candidate) => candidate === color) ?? null;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Derive toolbar state from the editor's current selection.
 *
 * The selector runs on every transaction but only triggers a React re-render
 * when the returned object is structurally different (useEditorState does
 * shallow equality on the result).
 */
export function useDocumentMenuState(editor: Editor | null): DocumentMenuState {
  const state = useEditorState({
    editor,
    selector: (ctx): DocumentMenuState => {
      if (!ctx.editor) return defaultState;

      const e = ctx.editor;
      return {
        // Inline marks
        isBold: e.isActive("bold"),
        canBold: e.can().chain().toggleBold().run(),
        isItalic: e.isActive("italic"),
        canItalic: e.can().chain().toggleItalic().run(),
        isStrike: e.isActive("strike"),
        canStrike: e.can().chain().toggleStrike().run(),
        isCode: e.isActive("code"),
        canCode: e.can().chain().toggleCode().run(),
        isHighlight: e.isActive("highlight"),
        canHighlight: e.can().chain().toggleHighlight().run(),
        highlightColor: activeHighlightColor(e),

        // Block nodes
        isHeading2: e.isActive("heading", { level: 2 }),
        isHeading3: e.isActive("heading", { level: 3 }),
        isBulletList: e.isActive("bulletList"),
        isOrderedList: e.isActive("orderedList"),
        isCodeBlock: e.isActive("codeBlock"),
        canCodeBlock: canToggleCodeBlock(e),
        codeBlockLanguage: (e.getAttributes("codeBlock").language as string) || "plaintext",
        isBlockquote: e.isActive("blockquote"),

        // Section context — which generic node the cursor is inside
        isSectionTitle: e.isActive("sectionTitle"),
        isParagraphSection: e.isActive("paragraphSection"),
        isRichSection: e.isActive("richSection"),
        isAtomicField: e.isActive("atomicField"),

        // History
        canUndo: e.can().chain().undo().run(),
        canRedo: e.can().chain().redo().run(),
      };
    },
  });

  return state ?? defaultState;
}
